<template>
  <div class='activity-feed-row'>
    <custom-tooltip popper-class="light-tooltip">
      <div slot='buttonContent'>
        <div class='activity-feed-row__name' :class='{"clickable": !isActivitySuccessful}' @click='openActivityDetailsPopup'>
          <v-icon class='activity-feed-row__indicator' :class='indicatorClass'>commit</v-icon>
          <div class="activity-feed-row__name__text">
            {{getName}}
          </div>
        </div>
      </div>
      <div slot="content">
        <slot name="tooltip">
          {{ getName }}
        </slot>
      </div>
    </custom-tooltip>
    <custom-tooltip popper-class="light-tooltip">
      <div slot='buttonContent'>
        <div class='activity-feed-row__progress' v-if='isActivitySuccessful'>
          <project-progress-table-cell
            :item="{progress: getProgress, status: getStatus}"
          />
        </div>
        <div class='activity-feed-row__hide' v-else>
          <span text class='hide-btn' @click.stop="updateActivity">
              {{ $t('hide.test.cards') }}
          </span>
        </div>
      </div>
      <div slot="content">
        <slot name="tooltip">
          {{ activityDate }}
        </slot>
      </div>
    </custom-tooltip>
  </div>
</template>

<script>
import { projectStatuses } from '@/constants/projects';
import ProjectProgressTableCell from '@/components/tableCells/ProjectProgressTableCell.vue';
import { ActivityFinishedStatus, activityType, configurationsImportSource } from '@/constants/activity';
import moment from 'moment/moment';
import CustomTooltip from '../common/CustomTooltip.vue';

function checkTimeDifference(date, type) {
  const currentTime = moment();
  const timeStore = moment(date);
  return currentTime.diff(timeStore, type);
}

export default {
  name: 'ActivityFeedRow',
  components: { ProjectProgressTableCell, CustomTooltip },
  props: {
    activity: {
      type: Object,
      required: true,
    },
  },
  methods: {
    async updateActivity() {
      await this.$emit('updateActivity', {activityId: this.activity.id, updates: { metaData: {...this.activity.metaData, hide: true}}});
    },
    async openActivityDetailsPopup() {
      if (!this.isActivitySuccessful) {
        await this.$emit('openActivityPopup', this.activity);
      }
    },
  },
  computed: {
    activityDate() {
      const hourDifference = checkTimeDifference(this.activity.startDate, 'hour');
      if (hourDifference > 24 || this.activity.status === ActivityFinishedStatus.FAILED) {
        return moment(this.activity.startDate).format('MMM DD HH:mm');
      }
      const minuteDifference = checkTimeDifference(this.activity.startDate, 'minute');
      const remainingMinutes = minuteDifference - (hourDifference * 60);
      if (minuteDifference > 60) {
        return `${hourDifference}h ${remainingMinutes}m`;
      }
      const secondDifference = checkTimeDifference(this.activity.startDate, 'second');
      const remainingSeconds = secondDifference - (remainingMinutes * 60);
      return `${remainingMinutes}m ${remainingSeconds}s`;
    },
    indicatorClass() {
      if (this.isFailedActivity) {
        return 'failed';
      }
      if (this.isWarningActivity) {
        return 'warning-activity';
      }
      return '';
    },
    isActivitySuccessful() {
      return !this.isWarningActivity && !this.isFailedActivity;
    },
    isWarningActivity() {
      return this.activity.metaData?.integrationFinishedStatus === ActivityFinishedStatus.FAILED;
    },
    isFailedActivity() {
      return this.activity.status === projectStatuses.FAILED;
    },
    getStatus() {
      return this.activity.progress === 100 ? projectStatuses.FINISHED : projectStatuses.IN_PROGRESS;
    },
    getProgress() {
      return Math.round(this.activity.progress);
    },
    getName() {
      let name = '';
      if (this.activity.type === activityType.CONFIGURATION_IMPORT) {
        if (this.activity.metaData?.type === configurationsImportSource.CSV) {
          name = `${this.$t('activity.log.feed.import.from.csv.activity')} - ${this.activity.metaData.tld}`;
        }
      } else if (this.activity.type === activityType.INTEGRATION_RESPONSE) {
        const integrationName = this.activity.metaData?.integrationName ? `- ${this.activity.metaData?.integrationName}` : '';
        name = `${this.$t('activity.log.feed.integration.response')} ${integrationName}`;
      } else if (this.activity.type === activityType.INSIGHTS) {
        name = `${this.$t('activity.log.feed.insights')} - ${this.activity.metaData.tld}`;
      } else if (this.activity.type === activityType.DNS_TWIST) {
        name = `${this.$t('activity.log.feed.dns.twist')} - ${this.activity.metaData.tld}`;
      } else if (this.activity.type === activityType.GENERATE_REPORT) {
        name = `${this.$t('activity.log.feed.generate.report')} - ${this.activity.metaData.tld}`;
      }
      return name;
    },
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/styles/design-system";
.activity-feed-row{
  display: flex;
  flex-direction: row;
  padding-left: $space-8;
  align-items: center;
  margin-top: rem(5);
  &__name{
    display: flex;
    width: rem(300);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    color: var(--text-primary);
    margin-top: $space-2;
    margin-right: $space-4;
    &.clickable {
      &:hover {
        cursor: pointer;
      }
    }
    &__text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &__progress{
    margin-top: rem(-4);
    text-align: left;
  }
  &__hide {
    margin-top: rem(3);
    .hide-btn {
      right: $space-4;
      color: var(--brand-primary);
      font-family: $font-family-regular;
      font-size: $font-size-base;
      letter-spacing: 0;
      &:hover {
        cursor: pointer;
      }
    }
  }
  &__indicator{
    transform: rotate(90deg);
    color: var(--severity-low);
    margin-top: rem(-5);
    &.failed {
      color: var(--severity-critical);
    }
    &.warning-activity {
      color: var(--severity-high);
    }
  }
}
</style>

<style lang='scss'>
.activity-feed-row {
  .project-progress-table-cell .percent {
    min-width: 37px;
  }
}
</style>
